import React, { useEffect, useMemo, useRef, useState } from "react";
import { database } from "../firebaseConfig";
import { ref, onValue, push, update, remove } from "firebase/database";
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL } from "firebase/storage";
import { authenticatedFetch, CLOUD_FUNCTION_URLS } from "../utils/cloudFunctions";
import { CREDIT_COST_DATA, calculateImageCredits } from "../config/creditPlans";
import ScreenComposerSettings from "./ScreenComposerSettings";
// Global voice-capture engine. The 'imageLabPrompt' preset asks the user
// six short questions and returns a fully-drafted AI image template (name,
// prompt with {{variables}}, aspect, engine, quality, alt text) that we
// drop straight into aiDraft — the manual editor still opens so the user
// can eyeball & tweak before saving.
import { useSpeakEngine } from "../speakEngine";
import "./ImageLab.css";
import "./SalesStudio.css";

// Reference photos (GIF cast) stay small. Email graphics are different: a tall
// screenshot used to be crushed on its long side, then the mail stretched that
// thin bitmap back out, so text turned to blocks. Mail images keep their width.
const BODY_IMAGE_MAX_BYTES = 500 * 1024;
const BODY_IMAGE_MAX_DIMENSION = 1000;
const EMAIL_IMAGE_MAX_BYTES = 1536 * 1024;
const EMAIL_IMAGE_MAX_WIDTH = 2000;
const EMAIL_IMAGE_MAX_HEIGHT = 8000;
const EMAIL_IMAGE_MIN_WIDTH = 1200;
const VALID_UPLOAD_TYPES = ["image/jpeg", "image/png", "image/gif", "image/webp"];

// Turn a human name into a safe, url/slug-friendly token used in {{image:token}}.
export const slugifyToken = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .replace(/_{2,}/g, "_")
    .slice(0, 40);

const hasTransparency = (img) => {
  try {
    const sample = document.createElement("canvas");
    sample.width = 32;
    sample.height = 32;
    const ctx = sample.getContext("2d");
    ctx.drawImage(img, 0, 0, 32, 32);
    const data = ctx.getImageData(0, 0, 32, 32).data;
    for (let i = 3; i < data.length; i += 4) {
      if (data[i] < 250) return true;
    }
    return false;
  } catch (_) {
    return true;
  }
};

// Shrink before upload. Defaults stay tight for reference photos.
// Email uploads pass a width cap and a higher byte budget, and a file that
// already fits is returned unchanged so a second encode can't soften the type.
// Never-throw: falls back to the original file on any error.
export const compressBodyImage = (file, options = {}) =>
  new Promise((resolve) => {
    const maxBytes = options.maxBytes || BODY_IMAGE_MAX_BYTES;
    const maxWidth = options.maxWidth || options.maxDimension || BODY_IMAGE_MAX_DIMENSION;
    const maxHeight = options.maxHeight || options.maxDimension || BODY_IMAGE_MAX_DIMENSION;
    const minWidth = options.minWidth || 0;
    try {
      if (!file || !file.type || file.type === "image/gif") {
        resolve(file);
        return;
      }
      const objectUrl = URL.createObjectURL(file);
      const img = new Image();
      img.onload = async () => {
        URL.revokeObjectURL(objectUrl);
        try {
          const longest = Math.max(img.width, img.height);
          const withinBox = img.width <= maxWidth && img.height <= maxHeight && longest <= (options.maxDimension || Infinity);
          if (options.keepOriginalWhenFits && file.size <= maxBytes && withinBox) {
            resolve(file);
            return;
          }
          const keepPng = file.type === "image/png" && hasTransparency(img);
          const mime = keepPng ? "image/png" : "image/jpeg";
          const encode = (w, h, quality) =>
            new Promise((res) => {
              const canvas = document.createElement("canvas");
              canvas.width = w;
              canvas.height = h;
              canvas.getContext("2d").drawImage(img, 0, 0, w, h);
              canvas.toBlob(res, mime, quality);
            });
          const dimsFor = (widthCap) => {
            const scale = Math.min(1, widthCap / img.width, maxHeight / img.height);
            return {
              width: Math.max(1, Math.round(img.width * scale)),
              height: Math.max(1, Math.round(img.height * scale)),
            };
          };
          const qualities = keepPng ? [undefined] : [0.92, 0.86, 0.8, 0.74];
          const widthFloor = Math.min(minWidth, img.width);
          let widthCap = Math.min(maxWidth, img.width);
          let best = null;
          for (let step = 0; step < 6; step++) {
            const dims = dimsFor(widthCap);
            for (let q = 0; q < qualities.length; q++) {
              // eslint-disable-next-line no-await-in-loop
              const blob = await encode(dims.width, dims.height, qualities[q]);
              if (!blob) break;
              best = blob;
              if (blob.size <= maxBytes) break;
            }
            if (best && best.size <= maxBytes) break;
            if (widthCap <= widthFloor) break;
            widthCap = Math.max(widthFloor, Math.round(widthCap * 0.9));
          }
          if (!best || best.size >= file.size) {
            resolve(file);
            return;
          }
          const ext = keepPng ? "png" : "jpg";
          const baseName = (file.name || "image").replace(/\.[^.]+$/, "");
          resolve(new File([best], `${baseName}.${ext}`, { type: mime }));
        } catch (_) {
          resolve(file);
        }
      };
      img.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        resolve(file);
      };
      img.src = objectUrl;
    } catch (_) {
      resolve(file);
    }
  });

const EMPTY_DRAFT = {
  id: null,
  name: "",
  token: "",
  tokenTouched: false,
  altText: "",
  maxWidth: 600,
  align: "center",
  imageUrl: "",
  storagePath: "",
};

// Dynamic AI image: instead of a fixed imageUrl it stores a prompt that is
// rendered per-recipient at send time. Optionally starts from a base image.
const EMPTY_AI_DRAFT = {
  id: null,
  name: "",
  token: "",
  tokenTouched: false,
  prompt: "",
  aspect: "square",
  quality: "low",
  model: "gpt-image-1-mini",
  altText: "",
  maxWidth: 600,
  align: "center",
  baseImageUrl: "",
  baseStoragePath: "",
  sourceMode: "prompt",
};

// One-time card preview: rendered with the cheap Nano Banana engine (not Pro)
// and generic sample data, purely so a new template has a thumbnail. The real
// per-recipient image is still generated later in Normal/Bulk mode.
const PREVIEW_MODEL = "gemini-3.1-flash-lite-image";
const PREVIEW_SAMPLE_VARIABLES = {
  business_name: "Aurora Studio",
  lead_name: "Alex Morgan",
  lead_firstname: "Alex",
  lead_lastname: "Morgan",
  lead_city: "Berlin",
  lead_country: "Germany",
  lead_type: "boutique design studio",
  lead_website: "https://example.com",
  recipient_name: "Alex Morgan",
  seo_visibility: "42.5",
  seo_keywords_total: "1,240",
  seo_keywords_top10: "86",
  seo_top_keyword: "interior design berlin",
  seo_main_competitor: "studio-lux.de",
  seo_backlinks: "3,400",
  seo_traffic: "3,200",
  seo_traffic_value: "5,800 €",
  seo_trend: "+12% (12M)",
  first_name: "Alex",
};
const PREVIEW_SAMPLE_CONTEXT =
  "Business: Aurora Studio\nIndustry: boutique design studio\nLocation: Berlin, Germany\n" +
  "Visibility over time (oldest→newest data points): Jan=31, Mar=34, May=38, Jul=42.5";

// Renders the one-time card thumbnail for an AI asset and stores it on the
// asset. Returns null on success, otherwise a user-facing error message.
// A GIF asset previews as the cheapest GIF engine, so the card thumbnail moves.
const GIF_MODELS = ["heygen-video-1", "seedance-2.0-mini", "grok-imagine-video-1.5-lite", "grok-imagine-video-1.5"];
const previewModelFor = (model) => (GIF_MODELS.includes(model) ? "heygen-video-1" : PREVIEW_MODEL);

export const renderAssetPreview = async ({ userId, assetId, token, model }) => {
  const assetPath = `/World/${userId}/settings/imageLab/${assetId}`;
  try {
    const res = await authenticatedFetch(CLOUD_FUNCTION_URLS.generateDynamicImage(), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        userId,
        token,
        contactId: null,
        variables: PREVIEW_SAMPLE_VARIABLES,
        contextText: PREVIEW_SAMPLE_CONTEXT,
        force: true,
        overrideModel: previewModelFor(model),
        preview: true,
      }),
    });
    const data = await res.json().catch(() => ({}));
    if (res.ok && data && data.imageUrl) {
      await update(ref(database, assetPath), {
        previewImageUrl: data.imageUrl,
        previewStoragePath: data.storagePath || "",
        previewUpdatedAt: Date.now(),
        previewPending: false,
      });
      return null;
    }
    await update(ref(database, assetPath), { previewPending: false }).catch(() => {});
    if (res.status === 402) {
      return `Not enough credits for the preview (needs ${data.creditsRequired ?? "a few"}, `
        + `balance ${data.creditBalance ?? 0}). Credits reset at the start of your next billing month.`;
    }
    return data.error || "Preview generation failed. Please try again.";
  } catch (err) {
    console.error("[ImageLab] preview generation failed:", err);
    await update(ref(database, assetPath), { previewPending: false }).catch(() => {});
    return "Preview generation failed. Please try again.";
  }
};

// Where the AI gets its reference image(s) from.
//  • prompt  : text prompt (+ optional uploaded base image) — the default.
//  • website : capture the recipient's LIVE website per send and feed it to the
//              model so a redesign keeps their real logo/colors/branding.
const SOURCE_MODE_OPTIONS = [
  { value: "prompt", label: "Text prompt (optionally with an uploaded base image)" },
  { value: "website", label: "The recipient's live website (auto-screenshot per contact)" },
];

export const ASPECT_OPTIONS = [
  { value: "square", label: "Square (1:1)" },
  { value: "landscape", label: "Landscape (3:2)" },
  { value: "portrait", label: "Portrait (2:3)" },
];

// AI image engine switch, cheapest → strongest:
//  • Budget (gpt-image-1-mini): fast & cheapest OpenAI lane for simple visuals.
//  • Nano Banana (gemini-3.1 Flash Image): best value for creative/photo edits.
//  • Nano Banana Pro (gemini-3-pro-image): flagship for ANALYSES/infographics —
//    accurate text + Google Search grounding, so labels/numbers land right.
//  • Premium (gpt-image-2): sharpest text & layout precision for dense designs.
// The "quality" tiers only affect the OpenAI models.
export const MODEL_OPTIONS = [
  { value: "gpt-image-1-mini", label: "Budget: cheapest, simple visuals (OpenAI)" },
  { value: "gemini-3.1-flash-lite-image", label: "Nano Banana: best value for creative (Google)" },
  { value: "flux-2-pro", label: "FLUX.2 Pro: EU-hosted, top value for photoreal & redesigns (Black Forest Labs)" },
  { value: "flux-2-max", label: "FLUX.2 Max: EU-hosted, highest quality & best edit consistency (Black Forest Labs)" },
  { value: "gemini-3-pro-image", label: "Nano Banana Pro: best for analyses & infographics (Google)" },
  { value: "gpt-image-2", label: "Premium: sharpest text & layout (OpenAI)" },
];

// ── Live price estimation ───────────────────────────────────────────
// The provider price matrix lives in config/creditCosts.json and is shared with
// the backend, which is what actually charges the user. Showing the credit price
// here (instead of our internal EUR cost, as this used to) means the number next
// to the Save button is exactly what will be deducted per generated image.
const IMAGE_MODELS = CREDIT_COST_DATA.imageGeneration.models;
// Generation resolution per aspect (must match the backend's aspectToSize).
const ASPECT_DIMS = CREDIT_COST_DATA.imageGeneration.aspectDims;

const formatCredits = (value) =>
  value == null || Number.isNaN(value) ? "—" : `${Number(value.toFixed(2))}`;

// Read a raster image's natural pixel dimensions from a Blob/File. Used so
// static uploads can store width/height → the email <img> can reserve the right
// box even when the image is blocked (spam folder). Never rejects (0/0 on fail).
const readImageDimensions = (blob) =>
  new Promise((resolve) => {
    try {
      const url = URL.createObjectURL(blob);
      const img = new Image();
      img.onload = () => {
        const dims = { width: img.naturalWidth || 0, height: img.naturalHeight || 0 };
        URL.revokeObjectURL(url);
        resolve(dims);
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        resolve({ width: 0, height: 0 });
      };
      img.src = url;
    } catch (_) {
      resolve({ width: 0, height: 0 });
    }
  });

// Ready-to-use starting points for the AI image editor. Clicking one prefills
// the whole draft; the user only refines. Prompts intentionally use the same
// {{variables}} as the text templates so they resolve per recipient at send.
const AI_IMAGE_PRESETS = [
  {
    id: "seo_chart",
    label: "SEO Visibility Chart",
    desc: "Line chart of visibility over 12 months",
    icon: "fa-chart-line",
    draft: {
      name: "SEO Visibility Chart",
      token: "seo_chart",
      aspect: "landscape",
      quality: "medium",
      model: "gemini-3-pro-image",
      altText: "SEO visibility trend for {{business_name}}",
      prompt:
        "Create a clean, modern line chart showing how the SEO visibility of {{business_name}} changed over the last 12 months. Use the exact 'Visibility over time' data points from the recipient context (oldest to newest, left to right). Make the trend obvious, add subtle gridlines, use a professional corporate palette on a white background, and place \"{{business_name}}\" as a small title in the top-right corner. No clutter, no fake numbers — only the provided data points.",
    },
  },
  {
    id: "website_redesign",
    label: "Before / After Website",
    desc: "Real site vs. redesign, side by side",
    icon: "fa-table-columns",
    draft: {
      name: "Before / After Website",
      token: "website_redesign",
      aspect: "landscape",
      quality: "high",
      model: "gemini-3-pro-image",
      sourceMode: "website",
      altText: "Website redesign mockup for {{business_name}}",
      prompt:
        "Create a sleek side-by-side 'before and after' comparison for {{business_name}}. LEFT: the recipient's CURRENT website exactly as shown in the attached screenshots (their real logo, colors and content). RIGHT: a modern, clean, conversion-optimized redesign of that same site — keep their exact logo, brand colors, typography and company name, just improve layout, hierarchy, hero and call-to-action. Show both inside realistic desktop browser window frames. Professional, on-brand, marketing-quality.",
    },
  },
  {
    id: "website_refresh",
    label: "Website Design Refresh",
    desc: "Redesign their LIVE site, keep the branding",
    icon: "fa-window-restore",
    draft: {
      name: "Website Design Refresh",
      token: "website_refresh",
      aspect: "landscape",
      quality: "high",
      model: "gemini-3-pro-image",
      sourceMode: "website",
      altText: "Modern redesign concept for {{business_name}}'s website",
      prompt:
        "Using the attached screenshots of {{business_name}}'s current website as the branding reference, design a modern, fresh homepage concept shown inside a realistic desktop browser window. Keep their exact logo, brand colors, typography and company name, but improve the layout, hierarchy, spacing, hero section and call-to-action so it looks like a premium, conversion-optimized 2026 redesign. Clean, professional, marketing-quality mockup.",
    },
  },
  {
    id: "local_hero",
    label: "Local Business Hero",
    desc: "Warm photoreal shot of their business",
    icon: "fa-store",
    draft: {
      name: "Local Business Hero",
      token: "local_hero",
      aspect: "landscape",
      quality: "medium",
      altText: "Hero image for {{business_name}}",
      prompt:
        "A warm, photorealistic hero image of a {{lead_type}} business in {{lead_city}}, inviting and on-brand, with subtle tasteful signage reading \"{{business_name}}\". Natural lighting, shallow depth of field, professional marketing photography style.",
    },
  },
  {
    id: "growth_rocket",
    label: "Growth Potential",
    desc: "Playful rocket + rising arrow",
    icon: "fa-rocket",
    draft: {
      name: "Growth Potential",
      token: "growth_rocket",
      aspect: "square",
      quality: "low",
      altText: "Growth potential illustration for {{business_name}}",
      prompt:
        "A playful yet professional flat-vector illustration of a rocket labeled \"{{business_name}}\" launching upward along a rising growth arrow, with a small {{lead_city}} skyline below. Energetic but clean colors, minimal background — symbolizing untapped growth potential.",
    },
  },
  {
    id: "competitor_gap",
    label: "Competitor Comparison",
    desc: "Bar chart: you vs. main competitor",
    icon: "fa-chart-column",
    draft: {
      name: "Competitor Comparison",
      token: "competitor_gap",
      aspect: "landscape",
      quality: "medium",
      model: "gemini-3-pro-image",
      altText: "Competitor visibility comparison for {{business_name}}",
      prompt:
        "A clean, minimal bar chart comparing {{business_name}} against its main competitor {{seo_main_competitor}} in online visibility. Clearly highlight the gap, add a short heading 'Where you stand today', professional corporate style on a white background. Use the recipient's SEO numbers where provided; do not invent data.",
    },
  },
  {
    id: "keyword_map",
    label: "Keyword Opportunity",
    desc: "Bullseye of quick-win keywords",
    icon: "fa-bullseye",
    draft: {
      name: "Keyword Opportunity Map",
      token: "keyword_map",
      aspect: "landscape",
      quality: "medium",
      model: "gemini-3-pro-image",
      altText: "Keyword opportunity map for {{business_name}}",
      prompt:
        "A modern infographic visualizing SEO keyword opportunities for {{business_name}}. Show a target/bullseye with the quick-win keyword \"{{seo_top_keyword}}\" highlighted in the center, surrounded by smaller keyword bubbles. Clean, data-driven look, corporate blue palette, white background.",
    },
  },
  {
    id: "traffic_money",
    label: "Traffic → Revenue",
    desc: "Visitors turning into money",
    icon: "fa-sack-dollar",
    draft: {
      name: "Traffic to Revenue",
      token: "traffic_money",
      aspect: "square",
      quality: "low",
      altText: "Traffic to revenue illustration for {{business_name}}",
      prompt:
        "A striking, minimal flat illustration showing website visitors flowing into a stack of euro coins for {{business_name}}, symbolizing traffic turning into revenue. Subtly display '{{seo_traffic}} visitors/month' as a small label. Modern style, green and blue tones, clean background.",
    },
  },
  {
    id: "city_postcard",
    label: "City Postcard Opener",
    desc: "Charming retro first-touch postcard",
    icon: "fa-envelope-open-text",
    draft: {
      name: "City Postcard Opener",
      token: "city_postcard",
      aspect: "landscape",
      quality: "low",
      altText: "Greeting postcard for {{business_name}}",
      prompt:
        "A charming vintage-style travel postcard from {{lead_city}} with bold retro lettering that reads 'Hello {{business_name}}!', featuring iconic {{lead_city}} landmarks illustrated in the background. Warm, friendly, eye-catching — perfect as a first-touch outreach opener.",
    },
  },
  {
    id: "attention_meme",
    label: "Attention Grabber",
    desc: "Tasteful, light-hearted meme style",
    icon: "fa-face-grin-wide",
    draft: {
      name: "Attention Grabber",
      token: "attention_meme",
      aspect: "square",
      quality: "low",
      altText: "Light-hearted attention-grabbing image for {{business_name}}",
      prompt:
        "A light-hearted, professional meme-style image for a cold outreach email to a {{lead_type}}. Tasteful humor about being hard to find on Google, with a subtle friendly nod to {{business_name}}. Bright and clean, not cheesy — designed to make the recipient smile and keep reading.",
    },
  },
  {
    id: "action_plan",
    label: "Growth Roadmap",
    desc: "3-step plan from Now to Goal",
    icon: "fa-route",
    draft: {
      name: "Growth Roadmap",
      token: "action_plan",
      aspect: "landscape",
      quality: "medium",
      model: "gemini-3-pro-image",
      altText: "Growth roadmap for {{business_name}}",
      prompt:
        "A clean, modern 3-step roadmap infographic titled 'Your growth plan, {{business_name}}', showing three milestones progressing from 'Now' to 'Goal'. Minimal corporate style, clear icons for each step, white background, professional palette.",
    },
  },
];

// The example is that preset rendered for one demo hotel (scripts/gen-imagelab-presets.js).
const PresetShot = ({ id }) => {
  const [missing, setMissing] = useState(false);
  if (missing) return null;
  return (
    <span className="wr-shot">
      <img src={`/img/sales-studio/imagelab/${id}.webp`} alt="" loading="lazy" onError={() => setMissing(true)} />
    </span>
  );
};

// One tool per app: "ai" (a prompt, rendered per contact), "image" (an upload)
// or "screen" (Screen Composer). Finished assets live on the Sales Studio
// overview, so this opens straight into the editor. `focusAssetId` edits one.
const ImageLab = ({ userId, tool = "ai", focusAssetId = null, onExit = null }) => {
  const focusOpenedRef = useRef(false);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState(() => (tool === "image" && !focusAssetId ? { ...EMPTY_DRAFT } : null));
  const [aiDraft, setAiDraft] = useState(() => (tool === "ai" && !focusAssetId ? { ...EMPTY_AI_DRAFT } : null));
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const fileInputRef = useRef(null);
  const aiFileInputRef = useRef(null);
  // Global SpeakEngine (mounted once in WorldLayout). Used only in the AI
  // Images tab: user describes the visual out loud, AI drafts every field
  // of aiDraft, editor opens for a final review.
  const { openCapture } = useSpeakEngine();

  useEffect(() => {
    if (!userId) return undefined;
    const assetsRef = ref(database, `/World/${userId}/settings/imageLab`);
    const unsub = onValue(
      assetsRef,
      (snap) => {
        const node = snap.exists() ? snap.val() : null;
        const list = node && typeof node === "object"
          ? Object.entries(node).map(([id, value]) => ({ id, ...value }))
          : [];
        list.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
        setAssets(list);
        setLoading(false);
      },
      () => setLoading(false)
    );
    return () => unsub();
  }, [userId]);

  const existingTokens = useMemo(
    () => new Set(assets.map((a) => a.token).filter(Boolean)),
    [assets]
  );

  const startCreate = () => {
    setMessage("");
    setDraft({ ...EMPTY_DRAFT });
  };

  const startEdit = (asset) => {
    setMessage("");
    setDraft({
      id: asset.id,
      name: asset.name || "",
      token: asset.token || "",
      tokenTouched: true,
      altText: asset.altText || "",
      maxWidth: asset.maxWidth || 600,
      align: asset.align || "center",
      imageUrl: asset.imageUrl || "",
      storagePath: asset.storagePath || "",
      imgWidth: asset.width || null,
      imgHeight: asset.height || null,
    });
  };

  const cancelEdit = () => {
    setDraft(null);
    setMessage("");
    if (onExit) onExit();
  };

  const handleNameChange = (name) => {
    setDraft((prev) => ({
      ...prev,
      name,
      token: prev.tokenTouched ? prev.token : slugifyToken(name),
    }));
  };

  const handleTokenChange = (value) => {
    setDraft((prev) => ({ ...prev, token: slugifyToken(value), tokenTouched: true }));
  };

  const handleFilePick = async (event, target = "static") => {
    const inputRef = target === "ai" ? aiFileInputRef : fileInputRef;
    const rawFile = event.target.files && event.target.files[0];
    if (inputRef.current) inputRef.current.value = "";
    if (!rawFile) return;
    if (!VALID_UPLOAD_TYPES.includes(rawFile.type)) {
      setMessage("Only JPG, PNG, GIF and WebP images are allowed (SVG is blocked by most email clients).");
      return;
    }
    if (rawFile.size > 15 * 1024 * 1024) {
      setMessage("The file is too large. Please use an image under 15 MB.");
      return;
    }
    try {
      setUploading(true);
      setMessage("Optimizing image...");
      const file = await compressBodyImage(rawFile, target === "ai" ? {} : {
        maxBytes: EMAIL_IMAGE_MAX_BYTES,
        maxWidth: EMAIL_IMAGE_MAX_WIDTH,
        maxHeight: EMAIL_IMAGE_MAX_HEIGHT,
        minWidth: EMAIL_IMAGE_MIN_WIDTH,
        keepOriginalWhenFits: true,
      });
      const byteCap = target === "ai" ? BODY_IMAGE_MAX_BYTES : EMAIL_IMAGE_MAX_BYTES;
      if (file.size > byteCap) {
        setMessage(`Image is still larger than ${Math.round(byteCap / 1024)} KB after compression. Please use a smaller image.`);
        setUploading(false);
        return;
      }
      setMessage("Uploading image...");
      const safeName = (file.name || "image").replace(/[^a-zA-Z0-9.]/g, "_");
      const storagePath = `email_body_images/${userId}/${Date.now()}_${safeName}`;
      const fileRef = storageRef(getStorage(), storagePath);
      await uploadBytes(fileRef, file);
      const downloadURL = await getDownloadURL(fileRef);
      if (target === "ai") {
        setAiDraft((prev) => ({ ...prev, baseImageUrl: downloadURL, baseStoragePath: storagePath }));
        setMessage("Base image ready. It will be edited per recipient using your prompt.");
      } else {
        // Capture natural dimensions so the email <img> can reserve the box
        // (graceful degradation when the image is blocked in a spam folder).
        const dims = await readImageDimensions(file);
        setDraft((prev) => ({
          ...prev,
          imageUrl: downloadURL,
          storagePath,
          imgWidth: dims.width || null,
          imgHeight: dims.height || null,
        }));
        setMessage("Image ready. Fill in the details and save.");
      }
    } catch (error) {
      console.error("[ImageLab] upload failed:", error);
      setMessage("Error uploading image: " + (error?.message || error));
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    if (!draft) return;
    const name = draft.name.trim();
    const token = slugifyToken(draft.token);
    if (!name) {
      setMessage("Please give the image a name.");
      return;
    }
    if (!token) {
      setMessage("Please provide a placeholder token (letters, numbers, underscores).");
      return;
    }
    if (!draft.imageUrl) {
      setMessage("Please upload an image first.");
      return;
    }
    const clash = assets.find((a) => a.token === token && a.id !== draft.id);
    if (clash) {
      setMessage(`The token "${token}" is already used by another image. Please choose a unique token.`);
      return;
    }
    const payload = {
      type: "static",
      name,
      token,
      altText: draft.altText.trim(),
      maxWidth: Math.min(1000, Math.max(50, parseInt(draft.maxWidth, 10) || 600)),
      align: ["left", "center", "right"].includes(draft.align) ? draft.align : "center",
      imageUrl: draft.imageUrl,
      storagePath: draft.storagePath || "",
      // Natural pixel dims (for blocked-image box reservation in emails).
      ...(draft.imgWidth ? { width: draft.imgWidth } : {}),
      ...(draft.imgHeight ? { height: draft.imgHeight } : {}),
      updatedAt: Date.now(),
    };
    try {
      setSaving(true);
      if (draft.id) {
        await update(ref(database, `/World/${userId}/settings/imageLab/${draft.id}`), payload);
      } else {
        const newRef = push(ref(database, `/World/${userId}/settings/imageLab`));
        await update(newRef, { ...payload, createdAt: Date.now() });
      }
      setDraft(null);
      setMessage("");
      if (onExit) onExit();
    } catch (error) {
      console.error("[ImageLab] save failed:", error);
      setMessage("Error saving image: " + (error?.message || error));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (asset) => {
    if (!asset || !asset.id) return;
    // eslint-disable-next-line no-restricted-globals, no-alert
    if (!window.confirm(`Delete "${asset.name}"? Templates using {{image:${asset.token}}} will no longer show this image.`)) {
      return;
    }
    try {
      await remove(ref(database, `/World/${userId}/settings/imageLab/${asset.id}`));
      if (onExit) onExit();
    } catch (error) {
      console.error("[ImageLab] delete failed:", error);
      setMessage("Error deleting image: " + (error?.message || error));
    }
  };

  // ── Dynamic AI image handlers ──────────────────────────────────────────────
  const startCreateAi = () => {
    setMessage("");
    setAiDraft({ ...EMPTY_AI_DRAFT });
  };

  // Prefill the AI editor from a ready-made template. The user only refines.
  // If the preset token already exists, add a numeric suffix so it stays unique.
  const startCreateAiFromPreset = (preset) => {
    setMessage("");
    const baseToken = preset.draft.token;
    let token = baseToken;
    let n = 2;
    while (assets.some((a) => a.token === token)) {
      token = `${baseToken}_${n++}`;
    }
    setAiDraft({
      ...EMPTY_AI_DRAFT,
      ...preset.draft,
      token,
      tokenTouched: true,
    });
  };

  // Voice-driven variant of startCreateAi. Opens the global SpeakEngine with
  // the 'imageLabPrompt' preset — the user speaks the brief, AI drafts every
  // aiDraft field (prompt with {{variables}}, aspect, engine, quality, alt
  // text), then the normal editor opens so the user still previews & saves
  // deliberately. Nothing is persisted to RTDB from this handler.
  const handleSpeakNewImage = () => {
    setMessage("");
    openCapture({
      preset: "imageLabPrompt",
      onResult: async (values) => {
        // Derive a unique token from the AI's suggestion or from the name,
        // reusing the same slug + clash-suffix logic as startCreateAiFromPreset.
        const baseToken = slugifyToken(values.token || values.name || "ai_image") || "ai_image";
        let token = baseToken;
        let n = 2;
        while (assets.some((a) => a.token === token)) {
          token = `${baseToken}_${n++}`;
        }

        // Guardrails — the SpeakEngine backend already validates enums, but
        // the caller does not trust the preset blindly for stateful fields.
        const aspect = ["square", "landscape", "portrait"].includes(values.aspect) ? values.aspect : "square";
        const sourceMode = values.sourceMode === "website" ? "website" : "prompt";
        const model = MODEL_OPTIONS.some((o) => o.value === values.model)
          ? values.model
          : "gemini-3.1-flash-lite-image";
        const quality = ["low", "medium", "high"].includes(values.quality) ? values.quality : "low";

        setAiDraft({
          ...EMPTY_AI_DRAFT,
          name: (values.name || "AI image").trim().slice(0, 80),
          token,
          tokenTouched: true,
          prompt: (values.prompt || "").trim(),
          aspect,
          sourceMode,
          model,
          quality,
          altText: (values.altText || "").trim(),
        });
        return "Template drafted — review and hit Save.";
      },
    });
  };

  const startEditAi = (asset) => {
    setMessage("");
    setAiDraft({
      id: asset.id,
      name: asset.name || "",
      token: asset.token || "",
      tokenTouched: true,
      prompt: asset.prompt || "",
      aspect: asset.aspect || "square",
      quality: asset.quality || "low",
      model: asset.model || "gpt-image-1-mini",
      altText: asset.altText || "",
      maxWidth: asset.maxWidth || 600,
      align: asset.align || "center",
      baseImageUrl: asset.baseImageUrl || "",
      baseStoragePath: asset.baseStoragePath || "",
      sourceMode: asset.sourceMode === "website" ? "website" : "prompt",
    });
  };

  useEffect(() => {
    if (!focusAssetId || loading || focusOpenedRef.current) return;
    focusOpenedRef.current = true;
    const asset = assets.find((a) => a.id === focusAssetId);
    if (!asset) {
      if (onExit) onExit();
      return;
    }
    if (asset.type === "ai") startEditAi(asset);
    else startEdit(asset);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusAssetId, loading, assets]);

  const cancelAiEdit = () => {
    setAiDraft(null);
    setMessage("");
    if (onExit) onExit();
  };

  const handleAiNameChange = (name) => {
    setAiDraft((prev) => ({
      ...prev,
      name,
      token: prev.tokenTouched ? prev.token : slugifyToken(name),
    }));
  };

  const handleAiTokenChange = (value) => {
    setAiDraft((prev) => ({ ...prev, token: slugifyToken(value), tokenTouched: true }));
  };

  const handleSaveAi = async () => {
    if (!aiDraft) return;
    const name = aiDraft.name.trim();
    const token = slugifyToken(aiDraft.token);
    if (!name) {
      setMessage("Please give the AI image a name.");
      return;
    }
    if (!token) {
      setMessage("Please provide a placeholder token (letters, numbers, underscores).");
      return;
    }
    if (!aiDraft.prompt.trim()) {
      setMessage("Please describe the image you want to generate.");
      return;
    }
    const clash = assets.find((a) => a.token === token && a.id !== aiDraft.id);
    if (clash) {
      setMessage(`The token "${token}" is already used by another image. Please choose a unique token.`);
      return;
    }
    const payload = {
      type: "ai",
      name,
      token,
      prompt: aiDraft.prompt.trim(),
      aspect: ["square", "landscape", "portrait"].includes(aiDraft.aspect) ? aiDraft.aspect : "square",
      quality: ["low", "medium", "high"].includes(aiDraft.quality) ? aiDraft.quality : "low",
      model: MODEL_OPTIONS.some((o) => o.value === aiDraft.model) ? aiDraft.model : "gpt-image-1-mini",
      altText: aiDraft.altText.trim(),
      maxWidth: Math.min(1000, Math.max(50, parseInt(aiDraft.maxWidth, 10) || 600)),
      align: ["left", "center", "right"].includes(aiDraft.align) ? aiDraft.align : "center",
      baseImageUrl: aiDraft.baseImageUrl || "",
      baseStoragePath: aiDraft.baseStoragePath || "",
      sourceMode: aiDraft.sourceMode === "website" ? "website" : "prompt",
      updatedAt: Date.now(),
    };
    try {
      setSaving(true);
      const prevAsset = aiDraft.id ? assets.find((a) => a.id === aiDraft.id) : null;
      let savedId;
      if (aiDraft.id) {
        await update(ref(database, `/World/${userId}/settings/imageLab/${aiDraft.id}`), payload);
        savedId = aiDraft.id;
      } else {
        const newRef = push(ref(database, `/World/${userId}/settings/imageLab`));
        await update(newRef, { ...payload, createdAt: Date.now() });
        savedId = newRef.key;
      }
      // Auto-render a fresh preview when the template is new or its visual
      // inputs changed — so the card always shows a representative thumbnail.
      const visualChanged =
        !prevAsset ||
        prevAsset.prompt !== payload.prompt ||
        prevAsset.aspect !== payload.aspect ||
        prevAsset.sourceMode !== payload.sourceMode ||
        (prevAsset.baseImageUrl || "") !== (payload.baseImageUrl || "");
      setAiDraft(null);
      setMessage("");
      if (visualChanged) {
        await update(ref(database, `/World/${userId}/settings/imageLab/${savedId}`), { previewPending: true });
        renderAssetPreview({ userId, assetId: savedId, token: payload.token });
      }
      if (onExit) onExit();
    } catch (error) {
      console.error("[ImageLab] AI save failed:", error);
      setMessage("Error saving AI image: " + (error?.message || error));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAi = async (asset) => {
    if (!asset || !asset.id) return;
    // eslint-disable-next-line no-restricted-globals, no-alert
    if (!window.confirm(`Delete "${asset.name}"? Templates using {{image:${asset.token}}} will no longer generate this image.`)) {
      return;
    }
    try {
      await remove(ref(database, `/World/${userId}/settings/imageLab/${asset.id}`));
      if (onExit) onExit();
    } catch (error) {
      console.error("[ImageLab] AI delete failed:", error);
      setMessage("Error deleting AI image: " + (error?.message || error));
    }
  };

  const aiEditorView = aiDraft && (
    <>
      <div className="il-editorBar">
        <span className="il-editorTitle">{aiDraft.id ? "Edit AI image" : "New AI image"}</span>
      </div>
      <section className="wr-step">
        <div className="wr-stepHead">
          <span className="wr-stepNum">1</span>
          <div>
            <h3 className="wr-stepTitle">The image</h3>
            <p className="wr-stepSub">One prompt, rendered fresh for every contact from their own data.</p>
          </div>
        </div>
        <div className="il-editorGrid">
          <div className="il-previewCol">
            <div className="il-previewFrame">
              {aiDraft.baseImageUrl ? (
                <img src={aiDraft.baseImageUrl} alt="base" />
              ) : aiDraft.sourceMode === "website" ? (
                <div className="il-previewEmpty il-previewEmptyWebsite">
                  <i className="fa-solid fa-globe" />
                  <span className="il-previewEmptyTitle">Input: the recipient's live website</span>
                  <span className="il-previewEmptySub">
                    We screenshot <code>{"{{lead_website}}"}</code> per recipient and feed it to the AI, so the
                    result keeps their real logo, colors and branding.
                  </span>
                </div>
              ) : (
                <div className="il-previewEmpty">
                  <i className="fa-solid fa-wand-magic-sparkles" />
                  <span>Generated live per recipient</span>
                </div>
              )}
            </div>
            <input
              ref={aiFileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/gif,image/webp"
              onChange={(e) => handleFilePick(e, "ai")}
              style={{ display: "none" }}
            />
            <button
              className="il-uploadBtn"
              onClick={() => aiFileInputRef.current && aiFileInputRef.current.click()}
              disabled={uploading}
            >
              <i className={`fa-solid ${uploading ? "fa-spinner fa-spin" : "fa-arrow-up-from-bracket"}`} />
              {uploading
                ? "Working…"
                : aiDraft.baseImageUrl
                ? "Replace base image"
                : aiDraft.sourceMode === "website"
                ? "Add extra reference (optional)"
                : "Add base image (optional)"}
            </button>
            {aiDraft.baseImageUrl && (
              <button
                type="button"
                className="il-btnGhost"
                onClick={() => setAiDraft((prev) => ({ ...prev, baseImageUrl: "", baseStoragePath: "" }))}
              >
                Remove base image
              </button>
            )}
            <p className="il-previewMeta">
              {aiDraft.sourceMode === "website"
                ? "The recipient's website is captured automatically. Any base image here is sent as an extra reference on top of it."
                : "Optional: start from a photo and let the AI adapt it per recipient."}
            </p>
          </div>

          <div className="il-fields">
            {!aiDraft.id && (
              <div className="il-field">
                <span className="il-fieldLabel">Start from a template</span>
                <div className="wr-directions" role="group" aria-label="Start from a template">
                  {AI_IMAGE_PRESETS.map((preset) => (
                    <button
                      type="button"
                      key={preset.id}
                      aria-pressed={aiDraft.prompt === preset.draft.prompt}
                      className={`wr-direction ${aiDraft.prompt === preset.draft.prompt ? "active" : ""}`}
                      onClick={() => startCreateAiFromPreset(preset)}
                    >
                      <PresetShot id={preset.id} />
                      <span className="wr-directionLabel">{preset.label}</span>
                      <span className="wr-styleLight">{preset.desc}</span>
                    </button>
                  ))}
                </div>
                <button type="button" className="altBtn altBtn--speak" onClick={handleSpeakNewImage}>
                  <i className="fa-solid fa-microphone" /> Speak instead
                </button>
              </div>
            )}

            <div className="il-field">
              <span className="il-fieldLabel">Image source</span>
              <div className="wr-demoFrames" role="radiogroup" aria-label="Image source">
                {SOURCE_MODE_OPTIONS.map((o) => (
                  <button
                    type="button"
                    key={o.value}
                    role="radio"
                    aria-checked={aiDraft.sourceMode === o.value}
                    className={`wr-demoFrame ${aiDraft.sourceMode === o.value ? "active" : ""}`}
                    onClick={() => setAiDraft((prev) => ({ ...prev, sourceMode: o.value }))}
                  >
                    {o.value === "website" ? "Their live website" : "Text prompt"}
                  </button>
                ))}
              </div>
              <span className="il-fieldHint">
                {aiDraft.sourceMode === "website"
                  ? "At send time we screenshot the recipient's website (from {{lead_website}}) and feed it to the model."
                  : "Generate from your prompt, optionally guided by an uploaded base image."}
              </span>
            </div>

            <label className="il-field">
              <span className="il-fieldLabel">Name</span>
              <input
                type="text"
                value={aiDraft.name}
                onChange={(e) => handleAiNameChange(e.target.value)}
                placeholder="e.g. Personalized storefront"
              />
            </label>

            <label className="il-field">
              <span className="il-fieldLabel">Placeholder token</span>
              <div className="il-tokenRow">
                <span className="il-tokenFix">{"{{image:"}</span>
                <input
                  type="text"
                  className="il-tokenInput"
                  value={aiDraft.token}
                  onChange={(e) => handleAiTokenChange(e.target.value)}
                  placeholder="personalized_storefront"
                />
                <span className="il-tokenFix">{"}}"}</span>
              </div>
              <span className="il-fieldHint">Paste this into a template body and a unique image is generated per recipient.</span>
            </label>

            <label className="il-field">
              <span className="il-fieldLabel">Image prompt</span>
              <textarea
                className="il-aiPromptInput"
                rows={5}
                value={aiDraft.prompt}
                onChange={(e) => setAiDraft((prev) => ({ ...prev, prompt: e.target.value }))}
                placeholder={"Describe the image. Use {{variables}} for recipient data, e.g.\nModern storefront mockup for {{business_name}}, clean and on-brand."}
              />
              <span className="il-fieldHint">
                Reference recipient fields with <code>{"{{business_name}}"}</code>, <code>{"{{lead_city}}"}</code>, etc. Recipient research context is added automatically.
              </span>
            </label>

            <label className="il-field">
              <span className="il-fieldLabel">Engine <em>(image model)</em></span>
              <select
                value={aiDraft.model}
                onChange={(e) => setAiDraft((prev) => ({ ...prev, model: e.target.value }))}
              >
                {MODEL_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </label>

            <div className="il-fieldRow">
              <label className="il-field">
                <span className="il-fieldLabel">Aspect</span>
                <select
                  value={aiDraft.aspect}
                  onChange={(e) => setAiDraft((prev) => ({ ...prev, aspect: e.target.value }))}
                >
                  {ASPECT_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </label>
              <label className="il-field">
                <span className="il-fieldLabel">Quality <em>(OpenAI only)</em></span>
                <select
                  value={aiDraft.quality}
                  onChange={(e) => setAiDraft((prev) => ({ ...prev, quality: e.target.value }))}
                  disabled={String(aiDraft.model || "").startsWith("gemini") || String(aiDraft.model || "").startsWith("flux")}
                >
                  <option value="low">Low (cheapest)</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </label>
            </div>

            <label className="il-field">
              <span className="il-fieldLabel">Alt text <em>(accessibility &amp; fallback)</em></span>
              <input
                type="text"
                value={aiDraft.altText}
                onChange={(e) => setAiDraft((prev) => ({ ...prev, altText: e.target.value }))}
                placeholder="Describe the image"
              />
            </label>

            <div className="il-fieldRow">
              <label className="il-field">
                <span className="il-fieldLabel">Max width (px)</span>
                <input
                  type="number"
                  min="50"
                  max="1000"
                  value={aiDraft.maxWidth}
                  onChange={(e) => setAiDraft((prev) => ({ ...prev, maxWidth: e.target.value }))}
                />
              </label>
              <label className="il-field">
                <span className="il-fieldLabel">Alignment</span>
                <select
                  value={aiDraft.align}
                  onChange={(e) => setAiDraft((prev) => ({ ...prev, align: e.target.value }))}
                >
                  <option value="left">Left</option>
                  <option value="center">Center</option>
                  <option value="right">Right</option>
                </select>
              </label>
            </div>
          </div>
        </div>
      </section>

      {message && <div className="il-msg">{message}</div>}

      <div className="il-editorActions">
        {(() => {
          const hasInputImage = aiDraft.sourceMode === "website" || !!aiDraft.baseImageUrl;
          const credits = calculateImageCredits({
            model: aiDraft.model,
            aspect: aiDraft.aspect,
            quality: aiDraft.quality,
            sourceMode: aiDraft.sourceMode,
            hasInputImage,
            promptChars: (aiDraft.prompt || "").length,
          });
          const dims = ASPECT_DIMS[aiDraft.aspect] || ASPECT_DIMS.square;
          const isOpenAi = IMAGE_MODELS[aiDraft.model]?.kind === "openai";
          const tip =
            "Credits deducted per generated image, based on engine, size and quality. "
            + "Charged once per recipient, because identical images are cached and reused for free. "
            + (aiDraft.sourceMode === "website"
              ? "Website mode also captures a live screenshot, which is included in this price."
              : "");
          return (
            <div className="il-costMeter" title={tip}>
              <span className="il-costMeterIcon">
                <i className="fa-solid fa-coins" />
              </span>
              <span className="il-costMeterBody">
                <span className="il-costMeterMain">
                  {formatCredits(credits)} <em>credits / image</em>
                </span>
                <span className="il-costMeterMeta">
                  {`${dims[0]}×${dims[1]}`}
                  {isOpenAi ? ` · ${aiDraft.quality} quality` : ""}
                  {aiDraft.sourceMode === "website" ? " · incl. screenshot" : ""}
                </span>
              </span>
            </div>
          );
        })()}
        <div className="il-editorActionsBtns">
          {aiDraft.id && (
            <button type="button" className="il-btnGhost wr-delete" onClick={() => handleDeleteAi(aiDraft)} disabled={saving}>
              <i className="fa-regular fa-trash-can" /> Delete
            </button>
          )}
          <button type="button" className="altBtn altBtn--save" onClick={handleSaveAi} disabled={saving || uploading}>
            {saving ? "Saving…" : "Save AI image"}
          </button>
        </div>
      </div>
    </>
  );

  const editorView = draft && (
    <>
      <div className="il-editorBar">
        <span className="il-editorTitle">{draft.id ? "Edit image" : "New image"}</span>
      </div>
      <section className="wr-step">
        <div className="wr-stepHead">
          <span className="wr-stepNum">1</span>
          <div>
            <h3 className="wr-stepTitle">The image</h3>
            <p className="wr-stepSub">Uploaded once, then dropped into any email with its tag. It travels inline, so it never breaks.</p>
          </div>
        </div>
        <div className="il-editorGrid">
        <div className="il-previewCol">
          <div className="il-previewFrame">
            {draft.imageUrl ? (
              <img src={draft.imageUrl} alt={draft.altText || "preview"} />
            ) : (
              <div className="il-previewEmpty">
                <i className="fa-regular fa-image" />
                <span>Your image appears here</span>
              </div>
            )}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/gif,image/webp"
            onChange={handleFilePick}
            style={{ display: "none" }}
          />
          <button
            className="il-uploadBtn"
            onClick={() => fileInputRef.current && fileInputRef.current.click()}
            disabled={uploading}
          >
            <i className={`fa-solid ${uploading ? "fa-spinner fa-spin" : "fa-arrow-up-from-bracket"}`} />
            {uploading ? "Working…" : draft.imageUrl ? "Replace image" : "Upload image"}
          </button>
          <p className="il-previewMeta">JPG · PNG · GIF · WebP · kept sharp for the email, up to 1.5 MB</p>
        </div>

        <div className="il-fields">
          <label className="il-field">
            <span className="il-fieldLabel">Name</span>
            <input
              type="text"
              value={draft.name}
              onChange={(e) => handleNameChange(e.target.value)}
              placeholder="e.g. Team photo"
            />
          </label>

          <label className="il-field">
            <span className="il-fieldLabel">Placeholder token</span>
            <div className="il-tokenRow">
              <span className="il-tokenFix">{"{{image:"}</span>
              <input
                type="text"
                className="il-tokenInput"
                value={draft.token}
                onChange={(e) => handleTokenChange(e.target.value)}
                placeholder="team_photo"
              />
              <span className="il-tokenFix">{"}}"}</span>
            </div>
            <span className="il-fieldHint">Paste this into a template body to position the image exactly.</span>
          </label>

          <label className="il-field">
            <span className="il-fieldLabel">Alt text <em>(accessibility &amp; fallback)</em></span>
            <input
              type="text"
              value={draft.altText}
              onChange={(e) => setDraft((prev) => ({ ...prev, altText: e.target.value }))}
              placeholder="Describe the image"
            />
          </label>

          <div className="il-fieldRow">
            <label className="il-field">
              <span className="il-fieldLabel">Max width (px)</span>
              <input
                type="number"
                min="50"
                max="1000"
                value={draft.maxWidth}
                onChange={(e) => setDraft((prev) => ({ ...prev, maxWidth: e.target.value }))}
              />
            </label>
            <label className="il-field">
              <span className="il-fieldLabel">Alignment</span>
              <select
                value={draft.align}
                onChange={(e) => setDraft((prev) => ({ ...prev, align: e.target.value }))}
              >
                <option value="left">Left</option>
                <option value="center">Center</option>
                <option value="right">Right</option>
              </select>
            </label>
          </div>
        </div>
        </div>
      </section>

      {message && <div className="il-msg">{message}</div>}

      <div className="il-editorActions">
        <div className="il-editorActionsBtns">
          {draft.id && (
            <button type="button" className="il-btnGhost wr-delete" onClick={() => handleDelete(draft)} disabled={saving}>
              <i className="fa-regular fa-trash-can" /> Delete
            </button>
          )}
          <button type="button" className="altBtn altBtn--save" onClick={handleSave} disabled={saving || uploading}>
            {saving ? "Saving…" : "Save image"}
          </button>
        </div>
      </div>
    </>
  );

  return (
    <div className="il wr">
      {tool === "screen" ? (
        <>
          <div className="il-editorBar">
            <span className="il-editorTitle">Screen Composer</span>
          </div>
          <section className="wr-step">
            <div className="wr-stepHead">
              <span className="wr-stepNum">1</span>
              <div>
                <h3 className="wr-stepTitle">Their website, live</h3>
                <p className="wr-stepSub">A screenshot or a scroll GIF of the contact's site, placed straight into the email.</p>
              </div>
            </div>
            <ScreenComposerSettings />
          </section>
        </>
      ) : tool === "image" ? (
        editorView
      ) : (
        aiEditorView
      )}
    </div>
  );
};

export default ImageLab;
